import { remove, uploadData } from 'aws-amplify/storage'
import { S3_STORAGE_SCOPE } from '@/config/portalEnvironment'
import { buildAmplifyBucketOptions, listS3ObjectKeys, normalizeS3Prefix } from '@/utils/s3Storage'
import {
  filterKnowledgeBasePreviewKeys,
  isKnowledgeBaseMetadataKey,
} from '@/mixins/Organization/OrganizationStorage.js'

export function resolveKnowledgeBaseS3Prefix(org = {}) {
  const fromApi = String(org.kb_s3_prefix ?? '').trim()
  if (fromApi) return normalizeS3Prefix(fromApi)

  const orgId = String(org.org_id ?? '').trim()
  if (orgId) return normalizeS3Prefix(`organizations/${orgId}/`)

  return ''
}

export function buildKnowledgeBaseObjectKey(org = {}, fileName = '') {
  const prefix = resolveKnowledgeBaseS3Prefix(org)
  const name = String(fileName || '').trim().replace(/^\/+/, '')
  if (!prefix || !name) return ''
  return `${prefix}${name}`
}

// Sentinel meaning "every product in this organisation". Must match the value
// the AI engine ORs into its retrieval filter (knowledge_base.py's own
// ALL_PRODUCTS) -- if the two drift, org-wide documents stop being found.
export const ALL_PRODUCTS = 'ALL'

export function buildKnowledgeBaseObjectMetadata(org = {}) {
  const orgId = String(org.org_id ?? org.orgId ?? '').trim()
  const internalOrgId = String(
    org.internal_org_id ?? org.internalOrgId ?? '',
  ).trim()
  const metadata = {}

  if (orgId) metadata.org_id = orgId
  if (internalOrgId) metadata.internal_org_id = internalOrgId
  // This is the ORGANISATION-level knowledge base -- it belongs to no single
  // product, so it is tagged for all of them. Without a context_name at all,
  // a product-scoped call filters it out entirely and the document is in the
  // index but unreachable, which is exactly what happened to the file
  // uploaded on 2026-08-10.
  metadata.context_name = ALL_PRODUCTS

  return metadata
}

export function buildKnowledgeBaseMetadataDocument(org = {}) {
  return {
    metadataAttributes: buildKnowledgeBaseObjectMetadata(org),
  }
}

export async function uploadKnowledgeBaseS3Object({
  path,
  data,
  contentType,
  metadata = {},
}) {
  const s3Path = String(path ?? '').trim()
  if (!s3Path) {
    throw new Error('Knowledge base upload path is missing.')
  }

  const normalizedMetadata = Object.fromEntries(
    Object.entries(metadata)
      .filter(([, value]) => value != null && String(value).trim())
      .map(([key, value]) => [key, String(value)]),
  )

  await uploadData({
    path: s3Path,
    data,
    options: {
      contentType,
      ...(Object.keys(normalizedMetadata).length
        ? { metadata: normalizedMetadata }
        : {}),
      ...buildAmplifyBucketOptions(S3_STORAGE_SCOPE.KNOWLEDGE_BASE),
    },
  }).result
}

export async function removeKnowledgeBaseS3File(s3Key) {
  const path = String(s3Key ?? '').trim()
  if (!path) {
    throw new Error('Knowledge base file path is missing.')
  }

  const bucketOptions = buildAmplifyBucketOptions(S3_STORAGE_SCOPE.KNOWLEDGE_BASE)

  await remove({
    path,
    options: bucketOptions,
  })

  const metadataPath = `${path}.metadata.json`
  try {
    await remove({
      path: metadataPath,
      options: bucketOptions,
    })
  } catch (err) {
    console.log('Knowledge base metadata sidecar remove skipped', err)
  }
}

export async function listKnowledgeBaseS3Keys(org = {}) {
  const prefix = resolveKnowledgeBaseS3Prefix(org)
  if (!prefix) return []

  try {
    const keys = await listS3ObjectKeys(prefix, S3_STORAGE_SCOPE.KNOWLEDGE_BASE)
    // Amplify's list({ listAll: true }) has no folder-scoping option here, so
    // it recurses into every subfolder under this prefix -- and product-level
    // KB uploads (createContext, yandle_admin_portal_1) land at exactly
    // organizations/{org_id}/{context_id}/..., one level under this same
    // org-level prefix. Without this filter, this org-level screen silently
    // showed every product's own KB files mixed in with the org's, with no
    // way to tell which was which. This is the org-level editor specifically
    // (every file uploaded here gets tagged context_name=ALL, see
    // buildKnowledgeBaseObjectMetadata above) -- product docs belong on that
    // product's own page, not here. Confirmed live 2026-08-20 (Ramu: "don't
    // show the knowledge bases which is uploaded at the Product level").
    const directChildren = keys.filter((key) => {
      const rest = key.startsWith(prefix) ? key.slice(prefix.length) : key
      return rest && !rest.includes('/')
    })
    return filterKnowledgeBasePreviewKeys(
      directChildren.filter((key) => !isKnowledgeBaseMetadataKey(key)),
    )
  } catch (err) {
    console.error('Knowledge base S3 list failed', err)
    return []
  }
}
